import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Shirt, Edit, Save, Palette, Sparkles, Wand2, Image as ImageIcon, CheckCircle, Upload } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { getAuthHeaders, hasToken, BACKEND_URL } from "@/lib/api";

const API = `${BACKEND_URL}/api`;

// Auth config helper
const authConfig = () => ({
  withCredentials: !hasToken(),
  headers: getAuthHeaders()
});

const DRESSCODE_PRESETS = [
  { id: "casual", label: "Casual", description: "Bequem & entspannt", emoji: "👕" },
  { id: "smart_casual", label: "Smart Casual", description: "Leger aber schick", emoji: "👔" },
  { id: "business", label: "Business", description: "Professionell & formell", emoji: "🎩" },
  { id: "cocktail", label: "Cocktail", description: "Elegant & festlich", emoji: "🥂" },
  { id: "formal", label: "Formal/Black Tie", description: "Abendgarderobe", emoji: "🎀" },
  { id: "theme", label: "Motto", description: "Nach Thema gekleidet", emoji: "🎭" },
  { id: "custom", label: "Individuell", description: "Eigene Beschreibung", emoji: "✨" },
];

export default function EventDresscode({ eventId, isOwner }) {
  const [dresscode, setDresscode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    preset: "",
    custom_description: "",
    color_theme: ""
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchDresscode();
  }, [eventId]);

  const fetchDresscode = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/dresscode`, authConfig());
      setDresscode(response.data);
      setFormData(response.data || { preset: "", custom_description: "", color_theme: "" });
    } catch (error) {
      console.error("Failed to fetch dresscode:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/events/${eventId}/dresscode`, formData, authConfig());
      setDresscode(formData);
      setEditing(false);
      toast.success("Dresscode gespeichert! 👗");
      // Ask to generate AI images if none exist
      if (!dresscode?.images?.length) {
        generateAIImages();
      }
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const generateAIImages = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/events/${eventId}/dresscode/generate-ai-images`, formData, authConfig());
      setDresscode(prev => ({ ...prev, images: response.data.images }));
      toast.success("KI-Style-Inspiration generiert! ✨");
    } catch (error) {
      console.error("AI Gen error:", error);
      toast.error("KI-Generierung fehlgeschlagen");
    } finally {
      setIsGenerating(false);
    }
  };

  const selectMainImage = async (imageUrl) => {
    try {
      await axios.put(`${API}/events/${eventId}/dresscode`, { image: imageUrl }, authConfig());
      setDresscode(prev => ({ ...prev, image: imageUrl }));
      setFormData(prev => ({ ...prev, image: imageUrl }));
      toast.success("Hintergrundbild ausgewählt! 🖼️");
    } catch (error) {
      toast.error("Auswahl fehlgeschlagen");
    }
  };

  const handleManualUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const resp = await axios.post(`${API}/events/${eventId}/dresscode-image`, formData, {
        ...authConfig(),
        headers: { ...authConfig().headers, "Content-Type": "multipart/form-data" }
      });
      setDresscode(prev => ({ ...prev, image: resp.data.dresscode_image }));
      setFormData(prev => ({ ...prev, image: resp.data.dresscode_image }));
      toast.success("Bild erfolgreich hochgeladen! 👗");
    } catch (error) {
      toast.error("Upload fehlgeschlagen");
    } finally {
      setIsUploading(false);
    }
  };

  const selectedPreset = DRESSCODE_PRESETS.find(p => p.id === (dresscode?.preset || formData.preset));

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-10 h-10 border-4 border-brand-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (editing || (!dresscode?.preset && isOwner)) {
    return (
      <div className="bg-white rounded-[2.5rem] p-8 border border-gray-100 shadow-xl space-y-8">
        <h3 className="text-2xl font-black text-brand-text flex items-center gap-3">
          <Shirt className="w-6 h-6 text-brand-accent" />
          Dresscode festlegen
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {DRESSCODE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              onClick={() => setFormData({ ...formData, preset: preset.id })}
              className={`p-4 rounded-2xl text-left transition-all border ${formData.preset === preset.id
                ? "bg-brand-accent/20 border-brand-accent shadow-[0_0_15px_rgba(193,154,107,0.3)]"
                : "bg-gray-50 border-gray-100 hover:bg-gray-100 hover:border-gray-200"
                }`}
            >
              <span className="text-3xl block mb-2">{preset.emoji}</span>
              <p className={`font-black text-sm uppercase tracking-widest ${formData.preset === preset.id ? "text-brand-accent" : "text-brand-text"}`}>
                {preset.label}
              </p>
              <p className="text-xs text-gray-500 mt-1 font-medium">{preset.description}</p>
            </button>
          ))}
        </div>

        {(formData.preset === "theme" || formData.preset === "custom") && (
          <div className="space-y-2">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-accent">Beschreibung</span>
            <Textarea
              placeholder="Beschreibe den Dresscode genauer..."
              value={formData.custom_description}
              onChange={(e) => setFormData({ ...formData, custom_description: e.target.value })}
              rows={3}
              className="rounded-2xl bg-gray-50 border-gray-200 text-brand-text placeholder:text-gray-400 focus:border-brand-accent focus:ring-brand-accent/50"
            />
          </div>
        )}

        <div className="space-y-4">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-accent">Eigenes Bild hochladen</span>
          <div className="flex items-center gap-4">
            <label className="flex-1 cursor-pointer">
              <div className={`h-24 rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-2 hover:bg-gray-50 transition-colors ${isUploading ? 'opacity-50' : ''}`}>
                <Upload className="w-6 h-6 text-gray-400" />
                <span className="text-[10px] font-bold text-gray-500 uppercase">Klicken zum Hochladen</span>
              </div>
              <input type="file" className="hidden" accept="image/*" onChange={handleManualUpload} disabled={isUploading} />
            </label>
            {formData.image && (
              <div className="w-24 h-24 rounded-2xl overflow-hidden border border-gray-100">
                <img src={formData.image} className="w-full h-full object-cover" alt="Selected" />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-accent">Farbthema (Optional)</span>
          <Input
            placeholder="z.B. Rot & Gold"
            value={formData.color_theme}
            onChange={(e) => setFormData({ ...formData, color_theme: e.target.value })}
            className="rounded-xl bg-gray-50 border-gray-200 text-brand-text placeholder:text-gray-400 focus:border-brand-accent focus:ring-brand-accent/50"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <Button onClick={handleSave} className="flex-1 rounded-xl bg-brand-accent text-white hover:bg-[#a07e54] font-bold uppercase tracking-widest h-12">
            <Save className="w-4 h-4 mr-2" />
            Speichern
          </Button>
          <Button
            onClick={generateAIImages}
            disabled={isGenerating || !formData.preset}
            variant="outline"
            className="flex-1 rounded-xl border-brand-accent text-brand-accent hover:bg-brand-accent/10 font-bold uppercase tracking-widest h-12"
          >
            {isGenerating ? (
              <div className="w-4 h-4 border-2 border-brand-accent border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Wand2 className="w-4 h-4 mr-2" />
            )}
            KI-Inspiration
          </Button>
          {dresscode?.preset && (
            <Button onClick={() => setEditing(false)} variant="ghost" className="rounded-xl text-gray-400 hover:text-brand-text hover:bg-gray-100">
              Abbrechen
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!dresscode?.preset) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400 space-y-4 bg-gray-50 rounded-[2.5rem] border-2 border-dashed border-gray-200">
        <Shirt className="w-16 h-16 opacity-30" />
        <p className="text-sm font-black uppercase tracking-widest">Noch kein Dresscode</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[3rem] p-10 border border-gray-100 shadow-xl relative overflow-hidden group">
      <div className="absolute top-0 right-0 w-64 h-64 bg-brand-accent/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />

      <div className="relative z-10 flex items-start justify-between mb-8">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 bg-brand-accent/20 rounded-[2rem] flex items-center justify-center text-5xl border border-brand-accent/30 shadow-[0_0_30px_rgba(193,154,107,0.2)]">
            {selectedPreset?.emoji}
          </div>
          <div>
            <p className="font-black text-2xl text-brand-text tracking-tight">{selectedPreset?.label}</p>
            <p className="text-brand-accent font-medium uppercase tracking-[0.2em] text-xs mt-1">{selectedPreset?.description}</p>
          </div>
        </div>
        {isOwner && (
          <button
            onClick={() => setEditing(true)}
            className="w-12 h-12 rounded-full bg-gray-50 hover:bg-brand-accent hover:text-white flex items-center justify-center transition-all border border-gray-100 text-gray-400"
          >
            <Edit className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="space-y-6">
        {dresscode.custom_description && (
          <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 italic text-brand-text/80 leading-relaxed shadow-inner">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 block mb-2 not-italic">Hinweis vom Gastgeber</span>
            "{dresscode.custom_description}"
          </div>
        )}

        {dresscode.color_theme && (
          <div className="flex items-center gap-4 text-sm font-medium bg-brand-accent/5 p-4 rounded-2xl border border-brand-accent/10">
            <Palette className="w-5 h-5 text-brand-accent" />
            <span className="text-brand-accent uppercase tracking-widest text-[10px] font-black">
              Farbthema: <span className="text-brand-text ml-2 text-sm">{dresscode.color_theme}</span>
            </span>
          </div>
        )}

        {/* AI Images Grid */}
        {dresscode.images && dresscode.images.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand-accent" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">KI Style-Inspiration {isOwner && "(Klicken zum Auswählen)"}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {dresscode.images.map((img, i) => {
                const isSelected = dresscode.image === img;
                return (
                  <div
                    key={i}
                    onClick={() => isOwner && selectMainImage(img)}
                    className={`group relative aspect-[3/4] rounded-2xl overflow-hidden bg-gray-100 border transition-all ${isOwner ? 'cursor-pointer' : ''} ${isSelected ? 'ring-4 ring-brand-accent border-brand-accent' : `border-gray-100 ${isOwner ? 'hover:scale-[1.02]' : ''}`}`}
                  >
                    <img src={img} className="w-full h-full object-cover" alt={`Inspiration ${i + 1}`} loading="lazy" />
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-brand-accent text-white p-1 rounded-full shadow-lg">
                        <CheckCircle className="w-4 h-4" />
                      </div>
                    )}
                    {isOwner && !isSelected && (
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-[10px] font-black text-white uppercase tracking-widest px-3 py-1 bg-brand-accent rounded-full">Auswählen</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
